import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import api from '../api';
import PageHeader from '../components/PageHeader';
import ConfirmDialog from '../components/ConfirmDialog';

const COLORS = {
  forestGreen: '#0F3D2E',
  deepGreen: '#003C3C',
  heritageGold: '#C9A14A',
  offWhite: '#FFFDF7',
  sand: '#F7F3EA',
  softBorder: '#E6D8BE',
  mutedText: '#746A5B',
  success: '#1F8F4D',
  warning: '#B85C38',
  danger: '#B54135',
};

const extractPayouts = (response) => {
  if (Array.isArray(response?.data?.payouts)) return response.data.payouts;
  if (Array.isArray(response?.data?.data?.payouts))
    return response.data.data.payouts;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const listFrom = (response) => {
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const money = (value) => `R${Number(value || 0).toFixed(2)}`;

const toDateInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
};

const formatOrderLabel = (value) =>
  String(value || '-')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getRecordDate = (record) =>
  record.createdAt || record.updatedAt || record.date;
const getPayoutTips = (payout) => {
  const savedTips = Number(payout.totalTips || payout.tips || 0);
  if (savedTips > 0) return savedTips;

  return (payout.orders || []).reduce(
    (sum, order) =>
      sum + Number(order.totalTip || order.totalTips || order.tipAmount || 0),
    0,
  );
};
const DASHBOARD_PAGE_SIZE = 5;

const getStatusBadgeStyle = (status) => {
  const value = String(status || '').toLowerCase();

  if (value.includes('suspended') || value.includes('rejected') || value.includes('closed')) {
    return {
      backgroundColor: '#FCEAE8',
      color: COLORS.danger,
      border: `1px solid #F1B7A7`,
    };
  }

  if (value.includes('pending')) {
    return {
      backgroundColor: '#FFF4D8',
      color: '#9B6B00',
      border: `1px solid #E4C36C`,
    };
  }

  if (value.includes('approved') || value.includes('active') || value.includes('open')) {
    return {
      backgroundColor: '#E7F6ED',
      color: COLORS.success,
      border: `1px solid #B7E2C5`,
    };
  }

  return {
    backgroundColor: '#F1E7D2',
    color: COLORS.forestGreen,
    border: `1px solid ${COLORS.softBorder}`,
  };
};

export default function Dashboard() {
  const [stats, setStats] = useState({
    outlets: 0,
    riders: 0,
    customers: 0,
    ordersToday: 0,
  });
  const [records, setRecords] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [restaurantPayouts, setRestaurantPayouts] = useState([]);
  const [offlineAlerts, setOfflineAlerts] = useState([]);
  const [riderPayoutPage, setRiderPayoutPage] = useState(1);
  const [restaurantPayoutPage, setRestaurantPayoutPage] = useState(1);
  const [recordPage, setRecordPage] = useState(1);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [confirmPayout, setConfirmPayout] = useState(null);
  const [confirmCustomer, setConfirmCustomer] = useState(null);
  const [actionId, setActionId] = useState(null);
  const [dashboardNotice, setDashboardNotice] = useState(null);

  const loadDashboardRecords = async () => {
    try {
      const [outletsResp, ridersResp, customersResp, ordersResp] =
        await Promise.all([
          api.fetchAdminRestaurants(),
          api.fetchRiders(),
          api.fetchCustomers({ page: 1, limit: 1000 }),
          api.fetchAllOrders({ page: 1, limit: 6 }),
        ]);

      const outlets = listFrom(outletsResp);
      const ridersList = listFrom(ridersResp);
      const customersList = listFrom(customersResp);
      const activeRiders = ridersList.filter((r) => r.isAccountActive);
      const orders = Array.isArray(ordersResp?.data) ? ordersResp.data : [];

      setRecentOrders(orders);

      setStats({
        outlets: outlets.length,
        riders: activeRiders.length,
        customers: customersList.length,
        ordersToday: Number(ordersResp?.summary?.today || 0),
      });

      const nextRecords = [
        ...customersList.map((customer) => ({
            id: `customer-${customer.id}`,
            rawId: customer.id,
            type: 'Customer',
            name:
              `${customer.firstName || customer.user?.firstName || ''} ${
                customer.lastName || customer.user?.lastName || ''
              }`.trim() ||
              'Customer',
            contact: customer.email || customer.user?.email || customer.phone || '-',
            phone: customer.phone || customer.user?.phone,
            email: customer.email || customer.user?.email,
            status: customer.isAccountActive ? 'Active' : 'Suspended',
            isAccountActive: customer.isAccountActive,
            address: customer.address,
            createdAt: customer.createdAt,
            updatedAt: customer.updatedAt,
          })),
          ...ridersList.map((rider) => ({
            id: `rider-${rider.id}`,
            rawId: rider.id,
            type: 'Rider',
            name:
              `${rider.firstName || ''} ${rider.lastName || ''}`.trim() ||
              'Rider',
            contact: rider.email || rider.phone || '-',
            phone: rider.phone,
            email: rider.email,
            status:
              rider.isAccountActive === false
                ? 'Suspended'
                : rider.approvalStatus ||
                  (rider.isApproved ? 'Approved' : 'Pending'),
            vehicleType: rider.vehicleType,
            createdAt: rider.createdAt,
            updatedAt: rider.updatedAt,
          })),
          ...outlets.map((outlet) => ({
            id: `outlet-${outlet.id}`,
            rawId: outlet.id,
            type: 'Outlet',
            name: outlet.name || 'Outlet',
            contact: outlet.phone || outlet.ownerEmail || '-',
            phone: outlet.phone,
            email: outlet.ownerEmail,
            status: outlet.status || (outlet.isOpen ? 'Open' : 'Closed'),
            address: outlet.address,
            createdAt: outlet.createdAt,
            updatedAt: outlet.updatedAt,
          })),
      ];

      setRecords(
        nextRecords.sort((a, b) => {
          const dateA = new Date(getRecordDate(a) || 0).getTime();
          const dateB = new Date(getRecordDate(b) || 0).getTime();
          return dateB - dateA;
        }),
      );
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      setDashboardNotice({
        type: 'error',
        text: error.message || 'Could not load dashboard records.',
      });
    }
  };

  useEffect(() => {
    loadDashboardRecords();
  }, []);

  const fetchPayoutNotifications = async () => {
    try {
      const [riderResp, restaurantResp, offlineResp] = await Promise.all([
        api.getRiderPayouts('pending'),
        api.getRestaurantPayouts('pending'),
        api.getRestaurantOfflineAlerts(),
      ]);
      setPayouts(extractPayouts(riderResp));
      setRestaurantPayouts(extractPayouts(restaurantResp));
      setOfflineAlerts(listFrom(offlineResp));
    } catch (error) {
      console.error('Failed to fetch payout summaries:', error);
    }
  };

  useEffect(() => {
    fetchPayoutNotifications();
    const interval = setInterval(fetchPayoutNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleMarkPaid = async (payout) => {
    try {
      setActionId(payout.id);
      await api.markRiderPayoutPaid(payout.id);
      setConfirmPayout(null);
      fetchPayoutNotifications();
    } catch (error) {
      console.error('Failed to mark payout paid:', error);
    } finally {
      setActionId(null);
    }
  };

  const handleCustomerStatusChange = async (record) => {
    const nextActive = record.status !== 'Active';

    try {
      setActionId(`customer-${record.rawId}`);
      setDashboardNotice(null);
      await api.toggleCustomerStatus(record.rawId, nextActive);
      setRecords((current) =>
        current.map((item) =>
          item.id === record.id
            ? {
                ...item,
                status: nextActive ? 'Active' : 'Suspended',
                isAccountActive: nextActive,
              }
            : item,
        ),
      );
      setSelectedRecord((current) =>
        current?.id === record.id
          ? {
              ...current,
              status: nextActive ? 'Active' : 'Suspended',
              isAccountActive: nextActive,
            }
          : current,
      );
      setDashboardNotice({
        type: 'success',
        text: `${record.name} has been ${nextActive ? 'activated' : 'suspended'}.`,
      });
    } catch (error) {
      setDashboardNotice({
        type: 'error',
        text: error.message || 'Could not update customer status.',
      });
    } finally {
      setActionId(null);
      setConfirmCustomer(null);
    }
  };

  const requestCustomerStatusChange = (record) => {
    const nextActive = record.status !== 'Active';
    setConfirmCustomer({
      record,
      title: `${nextActive ? 'Activate' : 'Suspend'} customer`,
      message: `${nextActive ? 'Activate' : 'Suspend'} ${record.name}?`,
      confirmLabel: nextActive ? 'Activate customer' : 'Suspend customer',
      tone: nextActive ? 'success' : 'danger',
    });
  };

  const filteredRecords = useMemo(() => {
    const q = search.trim().toLowerCase();
    return records.filter((record) => {
      const text = [
        record.type,
        record.name,
        record.contact,
        record.status,
        record.phone,
        record.email,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (q && !text.includes(q)) return false;
      if (typeFilter && record.type !== typeFilter) return false;
      if (statusFilter && record.status !== statusFilter) return false;

      const dateValue = getRecordDate(record);
      const dateKey = toDateInput(dateValue);
      if (fromDate && (!dateKey || dateKey < fromDate)) return false;
      if (toDate && (!dateKey || dateKey > toDate)) return false;

      return true;
    });
  }, [records, search, typeFilter, statusFilter, fromDate, toDate]);

  const recordPages = Math.max(
    1,
    Math.ceil(filteredRecords.length / DASHBOARD_PAGE_SIZE),
  );
  const safeRecordPage = Math.min(recordPage, recordPages);
  const paginatedRecords = filteredRecords.slice(
    (safeRecordPage - 1) * DASHBOARD_PAGE_SIZE,
    safeRecordPage * DASHBOARD_PAGE_SIZE,
  );

  const statuses = useMemo(() => {
    return [
      ...new Set(records.map((record) => record.status).filter(Boolean)),
    ].sort();
  }, [records]);

  const pendingTotal = payouts.reduce(
    (sum, payout) => sum + Number(payout.amount || 0),
    0,
  );
  const restaurantPendingTotal = restaurantPayouts.reduce(
    (sum, payout) => sum + Number(payout.amount || 0),
    0,
  );
  const riderPayoutPages = Math.max(
    1,
    Math.ceil(payouts.length / DASHBOARD_PAGE_SIZE),
  );
  const restaurantPayoutPages = Math.max(
    1,
    Math.ceil(restaurantPayouts.length / DASHBOARD_PAGE_SIZE),
  );
  const paginatedRiderPayouts = payouts.slice(
    (riderPayoutPage - 1) * DASHBOARD_PAGE_SIZE,
    riderPayoutPage * DASHBOARD_PAGE_SIZE,
  );
  const paginatedRestaurantPayouts = restaurantPayouts.slice(
    (restaurantPayoutPage - 1) * DASHBOARD_PAGE_SIZE,
    restaurantPayoutPage * DASHBOARD_PAGE_SIZE,
  );

  useEffect(() => {
    setRiderPayoutPage(1);
  }, [payouts.length]);

  useEffect(() => {
    setRestaurantPayoutPage(1);
  }, [restaurantPayouts.length]);

  useEffect(() => {
    setRecordPage(1);
  }, [search, typeFilter, statusFilter, fromDate, toDate]);

  return (
    <Layout>
      <PageHeader
        eyebrow="Today"
        title="Welcome, Admin"
        subtitle="A focused view of riders, customers, restaurants, and weekly payouts."
      />

      <div style={styles.grid}>
        <div style={styles.card}>
          <h3>Total Outlets</h3>
          <p>{stats.outlets}</p>
          <span style={styles.cardMeta}>Restaurant accounts</span>
        </div>
        <div style={styles.card}>
          <h3>Active Riders</h3>
          <p>{stats.riders}</p>
        </div>
        <div style={styles.card}>
          <h3>Orders Today</h3>
          <p>{stats.ordersToday}</p>
          <span style={styles.cardMeta}>Placed since midnight</span>
        </div>
        <div style={styles.card}>
          <h3>Customers</h3>
          <p>{stats.customers}</p>
        </div>
      </div>

      <section style={styles.tablePanel}>
        <div style={styles.panelTop}>
          <div>
            <h3 style={styles.panelTitle}>Recent Orders</h3>
            <p style={styles.panelSub}>
              The latest activity across delivery, pickup, and dine-in orders.
            </p>
          </div>
          <Link to="/orders" style={styles.viewAllLinkTop}>
            View all orders
          </Link>
        </div>

        <div style={styles.tableShell}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Order</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Restaurant</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Total</th>
                <th style={styles.th}>Placed</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.length === 0 ? (
                <tr>
                  <td colSpan="7" style={styles.emptyCell}>
                    No orders have been placed yet.
                  </td>
                </tr>
              ) : (
                recentOrders.map((order) => (
                  <tr key={order.id}>
                    <td style={styles.td}>
                      <Link to="/orders" style={styles.orderLink}>
                        #{order.id}
                      </Link>
                    </td>
                    <td style={styles.td}>
                      <strong>{order.customer?.name || 'Customer'}</strong>
                      <div style={styles.muted}>
                        {order.customer?.phone || order.customer?.email || '-'}
                      </div>
                    </td>
                    <td style={styles.td}>{order.restaurant?.name || '-'}</td>
                    <td style={styles.td}>
                      {formatOrderLabel(
                        order.orderMode || order.deliveryMode || 'delivery',
                      )}
                    </td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.statusBadge,
                          ...getStatusBadgeStyle(order.status),
                        }}
                      >
                        {formatOrderLabel(order.status)}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <strong>{money(order.totalPrice)}</strong>
                    </td>
                    <td style={styles.td}>
                      {formatDateTime(order.createdAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {offlineAlerts.length > 0 && (
        <section style={styles.alertPanel}>
          <div style={styles.panelTop}>
            <div>
              <h3 style={styles.alertTitle}>
                Restaurants Offline During Operating Hours
              </h3>
              <p style={styles.panelSub}>
                These restaurants should be live now according to their saved
                hours.
              </p>
            </div>
            <strong style={styles.alertCount}>{offlineAlerts.length}</strong>
          </div>

          <div style={styles.tableShell}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Restaurant</th>
                  <th style={styles.th}>Today</th>
                  <th style={styles.th}>Contact</th>
                  <th style={styles.th}>Last Updated</th>
                </tr>
              </thead>
              <tbody>
                {offlineAlerts.slice(0, 6).map((alert) => (
                  <tr key={alert.id}>
                    <td style={styles.td}>
                      <strong>{alert.name || 'Restaurant'}</strong>
                      <div style={styles.muted}>{alert.address || '-'}</div>
                    </td>
                    <td style={styles.td}>{alert.todayHours || 'Open now'}</td>
                    <td style={styles.td}>
                      {alert.phone || alert.ownerEmail || '-'}
                    </td>
                    <td style={styles.td}>{formatDate(alert.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {dashboardNotice && (
        <div
          style={{
            ...styles.notice,
            ...(dashboardNotice.type === 'error'
              ? styles.noticeError
              : styles.noticeSuccess),
          }}
        >
          {dashboardNotice.text}
        </div>
      )}

      <section style={styles.tablePanel}>
        <div style={styles.panelTop}>
          <div>
            <h3 style={styles.panelTitle}>Platform Records</h3>
            <p style={styles.panelSub}>
              Search users and outlets, filter by date, then click a row to view
              the record.
            </p>
          </div>
          <strong style={styles.recordCount}>{filteredRecords.length}</strong>
        </div>

        <div style={styles.filters}>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, email, phone, status..."
            style={styles.searchInput}
          />
          <select
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value)}
            style={styles.select}
          >
            <option value="">All types</option>
            <option value="Customer">Customers</option>
            <option value="Rider">Riders</option>
            <option value="Outlet">Outlets</option>
          </select>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            style={styles.select}
          >
            <option value="">All statuses</option>
            {statuses.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={fromDate}
            onChange={(event) => setFromDate(event.target.value)}
            style={styles.dateInput}
          />
          <input
            type="date"
            value={toDate}
            onChange={(event) => setToDate(event.target.value)}
            style={styles.dateInput}
          />
          <button
            type="button"
            style={styles.clearButton}
            onClick={() => {
              setSearch('');
              setTypeFilter('');
              setStatusFilter('');
              setFromDate('');
              setToDate('');
            }}
          >
            Clear
          </button>
        </div>

        <div style={styles.tableShell}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Contact</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Created</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan="6" style={styles.emptyCell}>
                    No records match your filters.
                  </td>
                </tr>
              ) : (
                paginatedRecords.map((record) => (
                  <tr
                    key={record.id}
                    style={styles.clickRow}
                    onClick={() => setSelectedRecord(record)}
                  >
                    <td style={styles.td}>{record.type}</td>
                    <td style={styles.td}>
                      <strong>{record.name}</strong>
                    </td>
                    <td style={styles.td}>{record.contact}</td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.statusBadge,
                          ...getStatusBadgeStyle(record.status),
                        }}
                      >
                        {record.status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {formatDate(getRecordDate(record))}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.recordActions}>
                        <button
                          type="button"
                          style={styles.smallActionButton}
                          onClick={(event) => {
                            event.stopPropagation();
                            setSelectedRecord(record);
                          }}
                        >
                          View
                        </button>
                        {record.type === 'Customer' ? (
                          <button
                            type="button"
                            style={{
                              ...styles.smallActionButton,
                              backgroundColor:
                                record.status === 'Active'
                                  ? COLORS.heritageGold
                                  : COLORS.success,
                            }}
                            disabled={actionId === `customer-${record.rawId}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              requestCustomerStatusChange(record);
                            }}
                          >
                            {actionId === `customer-${record.rawId}`
                              ? 'Saving...'
                              : record.status === 'Active'
                              ? 'Suspend'
                              : 'Activate'}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div style={styles.paginationBar}>
          <button
            type="button"
            style={styles.pageButton}
            disabled={safeRecordPage === 1}
            onClick={() => setRecordPage((page) => Math.max(1, page - 1))}
          >
            Previous
          </button>
          <span style={styles.pageText}>
            Page {safeRecordPage} of {recordPages}
          </span>
          <button
            type="button"
            style={styles.pageButton}
            disabled={safeRecordPage === recordPages}
            onClick={() =>
              setRecordPage((page) => Math.min(recordPages, page + 1))
            }
          >
            Next
          </button>
        </div>
      </section>

      <div style={styles.payoutPanel}>
        <div style={styles.panelTop}>
          <div>
            <h3 style={styles.panelTitle}>Pending Rider Payouts</h3>
            <p style={styles.panelSub}>
              {payouts.length} payout{payouts.length === 1 ? '' : 's'} awaiting
              Monday payment.
            </p>
          </div>
          <div style={styles.pendingTotal}>{money(pendingTotal)}</div>
        </div>

        {payouts.length === 0 ? (
          <div style={styles.emptyPayout}>
            No rider payout payments are pending.
          </div>
        ) : (
          <div style={styles.tableShell}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Rider</th>
                  <th style={styles.th}>Payable</th>
                  <th style={styles.th}>Deliveries</th>
                  <th style={styles.th}>Tips</th>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRiderPayouts.map((payout) => {
                  const riderName =
                    `${payout.rider?.firstName || ''} ${
                      payout.rider?.lastName || ''
                    }`.trim() || 'Rider';

                  return (
                    <tr key={payout.id}>
                      <td style={styles.td}>
                        <strong>{riderName}</strong>
                      </td>
                      <td style={styles.td}>{formatDate(payout.payableOn)}</td>
                      <td style={styles.td}>{payout.deliveriesCount || 0}</td>
                      <td style={styles.td}>
                        <strong>{money(getPayoutTips(payout))}</strong>
                      </td>
                      <td style={styles.td}>
                        <strong>{money(payout.amount)}</strong>
                      </td>
                      <td style={styles.td}>
                        <button
                          style={styles.payButton}
                          onClick={() => setConfirmPayout(payout)}
                        >
                          Mark Paid
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={styles.paginationBar}>
              <button
                type="button"
                style={styles.pageButton}
                disabled={riderPayoutPage === 1}
                onClick={() =>
                  setRiderPayoutPage((page) => Math.max(1, page - 1))
                }
              >
                Previous
              </button>
              <span style={styles.pageText}>
                Page {riderPayoutPage} of {riderPayoutPages}
              </span>
              <button
                type="button"
                style={styles.pageButton}
                disabled={riderPayoutPage === riderPayoutPages}
                onClick={() =>
                  setRiderPayoutPage((page) =>
                    Math.min(riderPayoutPages, page + 1),
                  )
                }
              >
                Next
              </button>
            </div>
          </div>
        )}

        <Link to="/rider-payouts" style={styles.viewAllLink}>
          Open payout manager
        </Link>
      </div>

      <div style={{ ...styles.payoutPanel, marginTop: 22 }}>
        <div style={styles.panelTop}>
          <div>
            <h3 style={styles.panelTitle}>Pending Restaurant Payouts</h3>
            <p style={styles.panelSub}>
              {restaurantPayouts.length} restaurant payout
              {restaurantPayouts.length === 1 ? '' : 's'} awaiting Monday
              payment.
            </p>
          </div>
          <div style={styles.pendingTotal}>{money(restaurantPendingTotal)}</div>
        </div>

        {restaurantPayouts.length === 0 ? (
          <div style={styles.emptyPayout}>
            No restaurant payout payments are pending.
          </div>
        ) : (
          <div style={styles.tableShell}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Restaurant</th>
                  <th style={styles.th}>Payable</th>
                  <th style={styles.th}>Orders</th>
                  <th style={styles.th}>Amount</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRestaurantPayouts.map((payout) => (
                  <tr key={payout.id}>
                    <td style={styles.td}>
                      <strong>{payout.restaurant?.name || 'Restaurant'}</strong>
                      <div style={styles.muted}>
                        {payout.restaurant?.phone ||
                          payout.restaurant?.ownerEmail ||
                          '-'}
                      </div>
                    </td>
                    <td style={styles.td}>{formatDate(payout.payableOn)}</td>
                    <td style={styles.td}>{payout.ordersCount || 0}</td>
                    <td style={styles.td}>
                      <strong>{money(payout.amount)}</strong>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.statusBadge}>Pending</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={styles.paginationBar}>
              <button
                type="button"
                style={styles.pageButton}
                disabled={restaurantPayoutPage === 1}
                onClick={() =>
                  setRestaurantPayoutPage((page) => Math.max(1, page - 1))
                }
              >
                Previous
              </button>
              <span style={styles.pageText}>
                Page {restaurantPayoutPage} of {restaurantPayoutPages}
              </span>
              <button
                type="button"
                style={styles.pageButton}
                disabled={restaurantPayoutPage === restaurantPayoutPages}
                onClick={() =>
                  setRestaurantPayoutPage((page) =>
                    Math.min(restaurantPayoutPages, page + 1),
                  )
                }
              >
                Next
              </button>
            </div>
          </div>
        )}

        <Link to="/restaurant-payouts" style={styles.viewAllLink}>
          Open restaurant payouts
        </Link>
      </div>

      {selectedRecord && (
        <div
          style={styles.modalOverlay}
          onClick={() => setSelectedRecord(null)}
        >
          <div
            style={styles.modal}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <div>
                <p style={styles.modalEyebrow}>{selectedRecord.type} Record</p>
                <h3 style={styles.modalTitle}>{selectedRecord.name}</h3>
              </div>
              <button
                style={styles.closeButton}
                onClick={() => setSelectedRecord(null)}
              >
                Close
              </button>
            </div>
            <div style={styles.detailGrid}>
              <span>ID</span>
              <strong>{selectedRecord.rawId}</strong>
              <span>Status</span>
              <strong>{selectedRecord.status}</strong>
              <span>Email</span>
              <strong>{selectedRecord.email || '-'}</strong>
              <span>Phone</span>
              <strong>{selectedRecord.phone || '-'}</strong>
              <span>Address</span>
              <strong>{selectedRecord.address || '-'}</strong>
              <span>Created</span>
              <strong>{formatDate(selectedRecord.createdAt)}</strong>
              <span>Updated</span>
              <strong>{formatDate(selectedRecord.updatedAt)}</strong>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmPayout}
        title="Mark rider payout paid"
        message={
          confirmPayout
            ? `Mark ${money(
                confirmPayout.amount,
              )} as paid? Only do this once the rider has been paid.`
            : ''
        }
        confirmLabel="Mark paid"
        tone="success"
        loading={!!actionId}
        onCancel={() => setConfirmPayout(null)}
        onConfirm={() => handleMarkPaid(confirmPayout)}
      />

      <ConfirmDialog
        open={!!confirmCustomer}
        title={confirmCustomer?.title}
        message={confirmCustomer?.message}
        confirmLabel={confirmCustomer?.confirmLabel}
        tone={confirmCustomer?.tone}
        loading={!!actionId}
        onCancel={() => setConfirmCustomer(null)}
        onConfirm={() => handleCustomerStatusChange(confirmCustomer.record)}
      />
    </Layout>
  );
}

const styles = {
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 16,
    marginBottom: 22,
  },
  card: {
    color: COLORS.forestGreen,
    backgroundColor: COLORS.offWhite,
    padding: 20,
    borderRadius: 8,
    border: `1px solid ${COLORS.softBorder}`,
    boxShadow: '0 8px 24px rgba(15,61,46,0.08)',
  },
  cardMeta: {
    display: 'block',
    color: COLORS.mutedText,
    fontWeight: 800,
    marginTop: 8,
  },
  tablePanel: {
    backgroundColor: COLORS.offWhite,
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 8,
    padding: 18,
    marginBottom: 22,
    boxShadow: '0 8px 24px rgba(15,61,46,0.08)',
  },
  payoutPanel: {
    backgroundColor: COLORS.offWhite,
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 8,
    padding: 18,
    boxShadow: '0 8px 24px rgba(15,61,46,0.08)',
  },
  alertPanel: {
    backgroundColor: '#FFF1E8',
    border: `1px solid #F1B7A7`,
    borderRadius: 8,
    padding: 18,
    marginBottom: 22,
    boxShadow: '0 8px 24px rgba(181,65,53,0.08)',
  },
  notice: {
    padding: '12px 14px',
    borderRadius: 8,
    marginBottom: 16,
    fontWeight: 800,
  },
  noticeSuccess: {
    backgroundColor: '#E7F6ED',
    color: COLORS.success,
    border: `1px solid ${COLORS.success}`,
  },
  noticeError: {
    backgroundColor: '#FCEAE8',
    color: COLORS.danger,
    border: `1px solid ${COLORS.danger}`,
  },
  alertTitle: { margin: 0, color: COLORS.danger },
  alertCount: { color: COLORS.danger, fontSize: 24 },
  panelTop: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 12,
  },
  panelTitle: { margin: 0, color: COLORS.forestGreen },
  panelSub: { margin: '5px 0 0', color: COLORS.mutedText },
  recordCount: { color: COLORS.heritageGold, fontSize: 24 },
  pendingTotal: { color: COLORS.warning, fontWeight: 900, fontSize: 24 },
  filters: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 10,
    marginBottom: 14,
    alignItems: 'stretch',
  },
  searchInput: {
    border: `1px solid ${COLORS.softBorder}`,
    backgroundColor: '#fff',
    borderRadius: 6,
    padding: '10px 12px',
    color: COLORS.deepGreen,
    minWidth: 0,
    width: '100%',
  },
  dateInput: {
    border: `1px solid ${COLORS.softBorder}`,
    backgroundColor: '#fff',
    borderRadius: 6,
    padding: '10px 12px',
    color: COLORS.deepGreen,
    minWidth: 0,
    width: '100%',
  },
  select: {
    border: `1px solid ${COLORS.softBorder}`,
    backgroundColor: '#fff',
    borderRadius: 6,
    padding: '10px 12px',
    color: COLORS.deepGreen,
    fontWeight: 800,
    minWidth: 0,
    width: '100%',
  },
  clearButton: {
    border: 'none',
    backgroundColor: COLORS.forestGreen,
    color: '#fff',
    borderRadius: 6,
    padding: '10px 14px',
    fontWeight: 900,
    cursor: 'pointer',
    width: '100%',
  },
  tableShell: {
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 8,
    overflowX: 'auto',
    overflowY: 'hidden',
  },
  muted: {
    color: COLORS.mutedText,
    fontSize: 12,
    marginTop: 4,
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    backgroundColor: '#F1E7D2',
    color: COLORS.forestGreen,
    textAlign: 'left',
    padding: '13px 14px',
    fontSize: 12,
    textTransform: 'uppercase',
  },
  td: {
    padding: '14px',
    color: COLORS.forestGreen,
    borderTop: `1px solid ${COLORS.softBorder}`,
  },
  clickRow: { cursor: 'pointer' },
  recordActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    minWidth: 150,
  },
  smallActionButton: {
    border: 'none',
    backgroundColor: COLORS.forestGreen,
    color: '#fff',
    borderRadius: 6,
    padding: '8px 10px',
    fontWeight: 900,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  emptyCell: {
    padding: 22,
    color: COLORS.mutedText,
    textAlign: 'center',
  },
  statusBadge: {
    display: 'inline-block',
    borderRadius: 999,
    padding: '5px 9px',
    fontSize: 12,
    fontWeight: 900,
  },
  payButton: {
    backgroundColor: COLORS.heritageGold,
    color: COLORS.forestGreen,
    border: 'none',
    padding: '8px 10px',
    borderRadius: 6,
    cursor: 'pointer',
    fontWeight: 900,
  },
  emptyPayout: {
    borderTop: `1px solid ${COLORS.softBorder}`,
    paddingTop: 14,
    color: COLORS.success,
    fontWeight: 800,
  },
  viewAllLink: {
    display: 'inline-block',
    marginTop: 12,
    color: COLORS.forestGreen,
    fontWeight: 900,
  },
  viewAllLinkTop: {
    color: COLORS.forestGreen,
    fontWeight: 900,
    whiteSpace: 'nowrap',
  },
  orderLink: {
    color: COLORS.forestGreen,
    fontWeight: 900,
  },
  paginationBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    padding: 12,
    borderTop: `1px solid ${COLORS.softBorder}`,
    backgroundColor: '#FBF6EA',
  },
  pageButton: {
    border: `1px solid ${COLORS.softBorder}`,
    backgroundColor: COLORS.offWhite,
    color: COLORS.forestGreen,
    borderRadius: 6,
    padding: '8px 11px',
    fontWeight: 900,
    cursor: 'pointer',
  },
  pageText: {
    color: COLORS.mutedText,
    fontWeight: 800,
    fontSize: 13,
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 61, 46, 0.42)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    zIndex: 1000,
  },
  modal: {
    width: 'min(560px, 100%)',
    backgroundColor: COLORS.offWhite,
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 8,
    padding: 22,
    boxShadow: '0 24px 80px rgba(15, 61, 46, 0.22)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 18,
  },
  modalEyebrow: {
    margin: 0,
    color: COLORS.heritageGold,
    fontWeight: 900,
    textTransform: 'uppercase',
    fontSize: 12,
  },
  modalTitle: { margin: '4px 0 0', color: COLORS.forestGreen, fontSize: 24 },
  closeButton: {
    alignSelf: 'flex-start',
    backgroundColor: COLORS.forestGreen,
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    padding: '8px 12px',
    fontWeight: 800,
    cursor: 'pointer',
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: '120px 1fr',
    gap: '12px 16px',
    color: COLORS.deepGreen,
  },
};
